import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  Heading1,
  PhoneWithIsdField,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import { Tabs, Tab } from "@mui/material";
import {
  useFetchCustomerInfo,
  useFetchGatewayTenants,
  useFetchPendingOrders,
} from "store/backend/assets";
import { useCountryIsdList } from "services/mdsService";
import { useTenantsByVerticalId } from "services/tenantService";
import Loader from "Shared/Loader/Loader";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { formatMonetaryAmount } from "utils/formatMonetaryAmount";
import {
  BUSINESS_VERTICALS_IDS,
  INDIA_COUNTRY_ID,
  LEARN_VERTICAL_ID,
  SEARCH_USER_TYPE,
} from "constants/config";
import {
  getOneOmsBaseUrl,
  getSearchUserUrl,
  isLearnOnlyRegion,
} from "constants/omsRegionConfig";
import {
  formatPhoneWithOptionalIsd,
  isIsdCodeSelected,
  REQUIRED_ISD_ERROR,
  sanitizePhone,
  validateInternationalPhone,
} from "utils/phoneValidation";
import {
  getGatewayTenantIdsForVertical,
  getUserVerticals,
} from "utils/userVerticals";
import { buildCustomerOrdersUrl } from "utils/customerFlowUrls";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import MultipleUsers from "components/MultipleUsers/MultipleUsers";

import styles from "./HomePage.module.scss";

const isViewCustomerDetailsButtonDisabled = (
  phoneNumber,
  uamId,
  searchType,
  selectedBusinessVertical,
  searchCountryId,
) => {
  if (!selectedBusinessVertical) {
    return true;
  }

  if (searchType === SEARCH_USER_TYPE.PHONE_NUMBER) {
    return !sanitizePhone(phoneNumber);
  }

  return uamId.trim() === "" || searchCountryId == null;
};

const getCountryIdFromIsdCode = (isdCode, countryIsdList) =>
  countryIsdList.find((country) => country.isd_code === isdCode)?.country_id ??
  null;

const buildCountryMenuItems = (countryIsdList) =>
  countryIsdList
    .map((country) => ({
      key: country.country_id,
      value: String(country.country_id),
      label: country.display_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const handleOnClickPendingOrder = (
  uamId,
  navigate,
  selectedBusinessVerticalId,
  countryId,
) => {
  navigate(
    buildCustomerOrdersUrl({
      uamId,
      verticalId: selectedBusinessVerticalId,
      countryId,
    }),
  );
};

const fetchCustomerInfoAndNavigate = ({
  searchType,
  phoneNumber,
  uamId,
  isdCode,
  navigate,
  openSnackbar,
  fetchCustomerInfo,
  selectedBusinessVertical,
  searchCountryId,
  setMultipleUsersList,
  setShowMultipleUsersDialog,
}) => {
  const fetchCustomerInfoPayload =
    searchType === SEARCH_USER_TYPE.PHONE_NUMBER
      ? {
          phone_number: phoneNumber,
          isd_code: isdCode,
          country_id: searchCountryId,
          selectedBusinessVertical: selectedBusinessVertical,
        }
      : {
          uam_id: uamId,
          country_id: searchCountryId,
          selectedBusinessVertical: selectedBusinessVertical,
        };

  fetchCustomerInfo(fetchCustomerInfoPayload, {
    onSuccess: (customerDetails) => {
      if (!Array.isArray(customerDetails)) {
        openSnackbar("Unexpected response format.", "error");
        return;
      }
      if (customerDetails.length > 1) {
        setMultipleUsersList(customerDetails);
        setShowMultipleUsersDialog(true);
        return;
      }
      if (customerDetails.length === 1) {
        const selectedUserId = customerDetails[0].uamId;
        const verticalId = customerDetails[0].verticalId;
        navigate(
          buildCustomerOrdersUrl({
            uamId: selectedUserId,
            verticalId,
            countryId: searchCountryId,
          }),
        );
        return;
      }
      openSnackbar("No customer found.", "error");
    },
    onError: (error) => {
      openSnackbar(error.message || "Failed to fetch customer info.", "error");
    },
  });
};

const handleClickViewCustomerDetailsButton = ({
  searchType,
  phoneNumber,
  uamId,
  isdCode,
  navigate,
  salesPersonPhoneNumber,
  openSnackbar,
  fetchCustomerInfo,
  selectedBusinessVertical,
  searchCountryId,
  setMultipleUsersList,
  setShowMultipleUsersDialog,
}) => {
  if (searchType === SEARCH_USER_TYPE.PHONE_NUMBER) {
    if (phoneNumber === salesPersonPhoneNumber) {
      openSnackbar(
        "Seems like you are creating an order with your number. Please change the mobile number and try again.",
        "error",
      );
      return;
    }

    if (!isIsdCodeSelected(isdCode)) {
      openSnackbar(REQUIRED_ISD_ERROR, "error");
      return;
    }

    if (!validateInternationalPhone(phoneNumber, isdCode)) {
      return;
    }
  } else if (!uamId.trim() || searchCountryId == null) {
    return;
  }

  try {
    const regionOptions = {
      countryId: searchCountryId,
      isdCode:
        searchType === SEARCH_USER_TYPE.PHONE_NUMBER ? isdCode : undefined,
    };
    // Validate regional URLs early (GCC may be blank until configured).
    getSearchUserUrl(regionOptions);
    getOneOmsBaseUrl(regionOptions);
  } catch (error) {
    openSnackbar(
      error.message || "Service is not configured for this country.",
      "error",
    );
    return;
  }

  fetchCustomerInfoAndNavigate({
    searchType,
    phoneNumber,
    uamId,
    isdCode,
    navigate,
    openSnackbar,
    fetchCustomerInfo,
    selectedBusinessVertical,
    searchCountryId,
    setMultipleUsersList,
    setShowMultipleUsersDialog,
  });
};

const getPendingAmount = (pendingOrder) => {
  if (!pendingOrder) return null;
  const currencyCode =
    pendingOrder.currency?.name ||
    pendingOrder.currency?.code ||
    pendingOrder.currency_code ||
    null;
  const pendingOrderAmount =
    currencyCode &&
    pendingOrder.amount != null &&
    pendingOrder.amount_paid != null
      ? pendingOrder.amount - pendingOrder.amount_paid
      : pendingOrder.inr_conversion_amount -
        pendingOrder.inr_conversion_amount_paid;
  return formatMonetaryAmount(
    reduceToFixedDecimals(pendingOrderAmount),
    currencyCode,
  );
};

const handleChangeBusinessVertical = (event, setSelectedBusinessVertical) => {
  const selectedBusinessVerticalId = event.target.value;
  setSelectedBusinessVertical(selectedBusinessVerticalId);
};

const getPendingOrderVerticalMenuItems = () => {
  const userVerticals = getUserVerticals();

  return BUSINESS_VERTICALS_IDS.filter((vertical) =>
    userVerticals.some(
      (userVertical) =>
        Number(userVertical.vertical_id) === Number(vertical.value),
    ),
  );
};

const getResolvedTenantCountryId = (tenantDetails) =>
  tenantDetails?.countryId != null
    ? tenantDetails.countryId
    : INDIA_COUNTRY_ID;

const HomePage = () => {
  const navigate = useNavigate();
  const pendingOrderVerticalMenuItems = useMemo(
    () => getPendingOrderVerticalMenuItems(),
    [],
  );
  const [salesPersonPhoneNumber] = useState(
    localStorage.getItem("phoneNumber"),
  );
  const [isdCode, setIsdCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [uamId, setUamId] = useState("");
  const [selectedSearchCountryId, setSelectedSearchCountryId] = useState("");
  const [searchType, setSearchType] = useState(SEARCH_USER_TYPE.PHONE_NUMBER);
  const { openSnackbar } = useSnackbarContext();
  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState("");
  const [selectedPendingVertical, setSelectedPendingVertical] = useState("");
  const [selectedPendingTenant, setSelectedPendingTenant] = useState("");
  const [pendingOrdersRequest, setPendingOrdersRequest] = useState(null);
  const [showMultipleUsersDialog, setShowMultipleUsersDialog] = useState(false);
  const [multipleUsersList, setMultipleUsersList] = useState([]);

  const { data: countryIsdList = [] } = useCountryIsdList();

  const countryMenuItems = useMemo(
    () => buildCountryMenuItems(countryIsdList),
    [countryIsdList],
  );

  const searchCountryId = useMemo(() => {
    if (searchType === SEARCH_USER_TYPE.PHONE_NUMBER) {
      return getCountryIdFromIsdCode(isdCode, countryIsdList);
    }
    return selectedSearchCountryId ? Number(selectedSearchCountryId) : null;
  }, [searchType, isdCode, countryIsdList, selectedSearchCountryId]);

  const businessVerticalMenuItems = useMemo(() => {
    const learnOnly = isLearnOnlyRegion({
      countryId: searchCountryId,
      isdCode:
        searchType === SEARCH_USER_TYPE.PHONE_NUMBER ? isdCode : undefined,
    });

    if (!learnOnly) {
      return BUSINESS_VERTICALS_IDS;
    }

    return BUSINESS_VERTICALS_IDS.filter(
      (vertical) => Number(vertical.value) === Number(LEARN_VERTICAL_ID),
    );
  }, [searchCountryId, searchType, isdCode]);

  const effectiveBusinessVertical = useMemo(() => {
    if (businessVerticalMenuItems.length === 1) {
      return String(businessVerticalMenuItems[0].value);
    }

    const isStillValid = businessVerticalMenuItems.some(
      (vertical) => String(vertical.value) === String(selectedBusinessVertical),
    );
    return isStillValid ? selectedBusinessVertical : "";
  }, [businessVerticalMenuItems, selectedBusinessVertical]);

  const gatewayTenantIds = useMemo(
    () => getGatewayTenantIdsForVertical(selectedPendingVertical),
    [selectedPendingVertical],
  );

  const {
    data: gatewayTenants = [],
    isPending: isGatewayTenantsLoading,
    isFetching: isGatewayTenantsFetching,
    error: gatewayTenantsError,
  } = useFetchGatewayTenants(selectedPendingVertical, gatewayTenantIds);

  const { data: verticalScopedTenants = [] } = useTenantsByVerticalId(
    selectedPendingVertical,
  );

  const tenantsByGatewayId = useMemo(
    () =>
      new Map(
        verticalScopedTenants.map((tenant) => [String(tenant.id), tenant]),
      ),
    [verticalScopedTenants],
  );

  const tenantMenuItems = useMemo(
    () =>
      gatewayTenants.map((tenant) => ({
        key: tenant.id,
        value: String(tenant.id),
        label: tenant.name,
        gatewayTenantId: tenant.gatewayTenantId,
        countryId:
          tenantsByGatewayId.get(String(tenant.gatewayTenantId))?.countryId ??
          null,
      })),
    [gatewayTenants, tenantsByGatewayId],
  );

  const isTenantListLoading = isGatewayTenantsLoading || isGatewayTenantsFetching;

  const isTenantFetchComplete =
    Boolean(selectedPendingVertical) &&
    (gatewayTenantIds.length === 0 ||
      (!isGatewayTenantsLoading && !isGatewayTenantsFetching));

  const hasNoTenantsForVertical =
    isTenantFetchComplete &&
    !gatewayTenantsError &&
    tenantMenuItems.length === 0;

  const selectedTenantValue =
    !isTenantListLoading &&
    tenantMenuItems.some((tenant) => tenant.value === selectedPendingTenant)
      ? selectedPendingTenant
      : "";

  const selectedTenantDetails = useMemo(
    () =>
      tenantMenuItems.find((tenant) => tenant.value === selectedTenantValue) ??
      null,
    [tenantMenuItems, selectedTenantValue],
  );

  const handlePendingVerticalChange = (event) => {
    setSelectedPendingVertical(event.target.value);
    setSelectedPendingTenant("");
    setPendingOrdersRequest(null);
  };

  const handlePendingTenantChange = (event) => {
    setSelectedPendingTenant(event.target.value);
    setPendingOrdersRequest(null);
  };

  const salesPersonId = localStorage.getItem("sales_person_id");

  const {
    isPending: isPendingOrderLoading,
    isFetching: isPendingOrderFetching,
    data: pendingOrders,
    error: pendingOrdersError,
  } = useFetchPendingOrders(salesPersonId, {
    countryId: pendingOrdersRequest?.countryId,
    enabled: Boolean(pendingOrdersRequest),
  });

  const isPendingOrdersResultCurrent =
    pendingOrdersRequest?.tenantId === selectedTenantValue &&
    pendingOrdersRequest?.countryId ===
      getResolvedTenantCountryId(selectedTenantDetails);

  const filteredPendingOrders = useMemo(() => {
    if (!isPendingOrdersResultCurrent || !pendingOrdersRequest?.tenantId) {
      return [];
    }

    return (pendingOrders || []).filter(
      (order) =>
        Number(order.tenant_id) === Number(pendingOrdersRequest.tenantId),
    );
  }, [pendingOrders, pendingOrdersRequest, isPendingOrdersResultCurrent]);

  const handleClickViewPendingOrdersButton = () => {
    if (!selectedTenantValue || !selectedTenantDetails) {
      return;
    }

    const countryId = getResolvedTenantCountryId(selectedTenantDetails);

    try {
      getOneOmsBaseUrl({ countryId });
    } catch (error) {
      openSnackbar(
        error.message || "Service is not configured for this country.",
        "error",
      );
      return;
    }

    setPendingOrdersRequest({
      tenantId: selectedTenantValue,
      countryId,
    });
  };

  const isViewPendingOrdersButtonDisabled =
    !selectedPendingVertical ||
    !selectedTenantValue ||
    isTenantListLoading ||
    hasNoTenantsForVertical;

  const isPendingOrdersLoading =
    isPendingOrderLoading || isPendingOrderFetching;

  const {
    mutate: fetchCustomerInfo,
    isPending: isfetchCustomerInformationLoading,
  } = useFetchCustomerInfo();

  return (
    <div className={styles.mainContainer}>
      <div className={styles.searchCustomerContainer}>
        <Tabs
          className={styles.tabsHeader}
          value={searchType}
          onChange={(e, newValue) => setSearchType(newValue)}
        >
          <Tab
            className={styles.tabLabel}
            label="Search by Phone Number"
            value={SEARCH_USER_TYPE.PHONE_NUMBER}
          />
          <Tab
            className={styles.tabLabel}
            label="Search by Customer ID"
            value={SEARCH_USER_TYPE.UAM_ID}
          />
        </Tabs>
        {searchType === SEARCH_USER_TYPE.PHONE_NUMBER ? (
          <div className={styles.inputContainer}>
            <Text>Please enter customer's registered mobile number.</Text>
            <PhoneWithIsdField
              id="search-customer-phone"
              label="Enter a Phone number"
              isdCode={isdCode}
              phone={phoneNumber}
              onIsdCodeChange={setIsdCode}
              onPhoneChange={setPhoneNumber}
            />
          </div>
        ) : (
          <div className={styles.inputContainer}>
            <Text>Please enter Customer ID.</Text>
            <Textfield
              value={uamId}
              handleOnChange={(e) => setUamId(e.target.value)}
              label="Enter a Customer ID"
            />
            <Text>Please select the customer's country.</Text>
            <SingleSelect
              tag="Select country"
              value={selectedSearchCountryId}
              menuItems={countryMenuItems}
              handleChange={(event) =>
                setSelectedSearchCountryId(event.target.value)
              }
              disabled={!countryMenuItems.length}
            />
          </div>
        )}
        <div className={styles.businessVertical}>
          <Text>Please select the business vertical</Text>
          <SingleSelect
            tag="Select business vertical"
            value={effectiveBusinessVertical}
            menuItems={businessVerticalMenuItems}
            handleChange={(event) =>
              handleChangeBusinessVertical(event, setSelectedBusinessVertical)
            }
          />
        </div>
        <div className={styles.viewCustomerDetailsButton}>
          <PrimaryButton
            onClick={() =>
              handleClickViewCustomerDetailsButton({
                searchType,
                phoneNumber,
                uamId,
                isdCode,
                navigate,
                salesPersonPhoneNumber,
                openSnackbar,
                fetchCustomerInfo,
                selectedBusinessVertical: effectiveBusinessVertical,
                searchCountryId,
                setMultipleUsersList,
                setShowMultipleUsersDialog,
              })
            }
            disabled={isViewCustomerDetailsButtonDisabled(
              phoneNumber,
              uamId,
              searchType,
              effectiveBusinessVertical,
              searchCountryId,
            )}
          >
            {isfetchCustomerInformationLoading ? (
              <Loader />
            ) : (
              "View Customer Details"
            )}
          </PrimaryButton>
        </div>
      </div>
      <div className={styles.cardContainer}>
        <Heading1 heading="Pending Orders" />
        <div className={styles.pendingOrdersFilters}>
          <div className={styles.pendingOrdersFilter}>
            <Text>Please select the business vertical</Text>
            <SingleSelect
              tag="Select business vertical"
              value={selectedPendingVertical}
              menuItems={pendingOrderVerticalMenuItems}
              handleChange={handlePendingVerticalChange}
              disabled={!pendingOrderVerticalMenuItems.length}
            />
          </div>
          <div className={styles.pendingOrdersFilter}>
            <Text>Please select the tenant</Text>
            {hasNoTenantsForVertical ? (
              <span className={styles.noTenantsPlaceholder}>No tenants found</span>
            ) : (
              <SingleSelect
                tag="Select tenant"
                value={selectedTenantValue}
                menuItems={tenantMenuItems}
                handleChange={handlePendingTenantChange}
                disabled={!selectedPendingVertical || isTenantListLoading}
              />
            )}
          </div>
        </div>
        <div className={styles.viewPendingOrdersButton}>
          <PrimaryButton
            onClick={handleClickViewPendingOrdersButton}
            disabled={isViewPendingOrdersButtonDisabled}
          >
            {isPendingOrdersLoading && pendingOrdersRequest ? (
              <Loader />
            ) : (
              "View Pending Orders"
            )}
          </PrimaryButton>
        </div>
        {gatewayTenantsError && (
          <ErrorMessage>
            {gatewayTenantsError.message || "Failed to fetch tenants."}
          </ErrorMessage>
        )}
        {pendingOrdersError && isPendingOrdersResultCurrent && (
          <ErrorMessage>
            {pendingOrdersError.message || "Error fetching Pending Orders"}
          </ErrorMessage>
        )}
        <hr className={styles.horizontalLine} />
        <div className={styles.cardList}>
          {!selectedPendingVertical ? (
            <span>
              Please select a business vertical and tenant to view pending orders
            </span>
          ) : isTenantListLoading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : hasNoTenantsForVertical ? (
            <span>No tenants found</span>
          ) : !selectedTenantValue ? (
            <span>Please select a tenant to view pending orders</span>
          ) : !pendingOrdersRequest || !isPendingOrdersResultCurrent ? (
            <span>
              Please click View Pending Orders to load orders for the selected
              tenant
            </span>
          ) : isPendingOrdersLoading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : filteredPendingOrders.length > 0 ? (
            filteredPendingOrders.map((pendingOrder, index) => (
              <div
                className={styles.card}
                key={pendingOrder.id ?? `pending-order-${index}`}
                onClick={() =>
                  handleOnClickPendingOrder(
                    pendingOrder.customer_id,
                    navigate,
                    pendingOrder.business_unit_id,
                    pendingOrdersRequest?.countryId,
                  )
                }
              >
                <DetailDisplay
                  title="Customer Id"
                  value={pendingOrder.customer_id}
                />
                <DetailDisplay
                  title="Billing Name"
                  value={pendingOrder.recipient_name}
                />
                <DetailDisplay
                  title=" Billing Phone Number"
                  value={formatPhoneWithOptionalIsd(
                    pendingOrder.masked_phone_no,
                    pendingOrder.isd_code,
                  )}
                />
                <DetailDisplay
                  title="Pending Amount"
                  value={getPendingAmount(pendingOrder)}
                />
                <DetailDisplay
                  title="Order Punch Date"
                  value={new Date(
                    pendingOrder.order_created_date,
                  ).toLocaleDateString()}
                />
              </div>
            ))
          ) : (
            <span>No Pending Orders</span>
          )}
        </div>
      </div>
      <Dialog
        open={showMultipleUsersDialog}
        handleClose={() => setShowMultipleUsersDialog(false)}
        height="auto"
      >
        <MultipleUsers
          customerDetails={multipleUsersList}
          handleOnSelectUser={(uamId, verticalId) => {
            setShowMultipleUsersDialog(false);
            navigate(
              buildCustomerOrdersUrl({
                uamId,
                verticalId,
                countryId: searchCountryId,
              }),
            );
          }}
        />
      </Dialog>
    </div>
  );
};

export default HomePage;
